import * as React from "react";
import { HUBSPOT_SERVICE_URL, IS_DEPLOYMENT_ENV_TEST, IS_HUBSPOT_INTEGRATION_ENABLED } from "../../../envVariables";
import { useAppSelector } from "../../../store/hooks";
import { selectIsCplnTeam } from "../../../store/userSlice";
import { selectHasAccount, selectHasOrg } from "../../../store/userDataSlice";
import { request } from "../../../services/cpln";
import { store } from "../../../store";
import { inputToBillingAccount } from "../utils";
import { BillingAccountCreateInput } from "./input";

const TWO_MINUTES_MS = 2 * 60 * 1000;

export function useInformHubspot(formId: string) {
  const isCplnTeam = useAppSelector(selectIsCplnTeam);
  const hasAccount = useAppSelector(selectHasAccount);
  const hasOrg = useAppSelector(selectHasOrg);

  const [isInforming, setIsInforming] = React.useState(false);
  const [isDone, setIsDone] = React.useState(false);
  const [trigger, setTrigger] = React.useState(0);

  function getSkip() {
    return !IS_HUBSPOT_INTEGRATION_ENABLED || isCplnTeam || hasAccount || hasOrg;
  }
  const [skip, setSkip] = React.useState(getSkip());
  React.useEffect(() => {
    setSkip(getSkip());
  }, [isDone, isCplnTeam, hasAccount, hasOrg]);

  React.useEffect(() => {
    if (skip) {
      return;
    }

    const id = setInterval(() => {
      setTrigger((x) => x + 1);
    }, TWO_MINUTES_MS);
    return () => {
      clearInterval(id);
    };
  }, [skip]);

  React.useEffect(() => {
    inform();
  }, [trigger]);

  async function inform() {
    if (isInforming) {
      return;
    }

    try {
      setIsInforming(true);
      const form = store.getState().form.instances[formId];
      if (!form) {
        setIsInforming(false);
        return;
      }
      const values: BillingAccountCreateInput = form.values as BillingAccountCreateInput;
      const account = inputToBillingAccount(values);
      await informHubspot(account);
      setIsInforming(false);
    } catch (e) {
      setIsInforming(false);
    }
  }

  async function informAccountCreation() {
    const canContinue = IS_HUBSPOT_INTEGRATION_ENABLED && !isCplnTeam;
    if (!canContinue) {
      return;
    }

    try {
      const form = store.getState().form.instances[formId];
      if (!form) return;
      const values: BillingAccountCreateInput = form.values as BillingAccountCreateInput;
      const account = inputToBillingAccount(values);
      const accountId = values.meta.createdAccount.id;
      await informHubspotAccountCreation(account, accountId);
    } catch (e) {}
  }

  return { setIsDone, informAccountCreation };
}

export async function informHubspot(account: any) {
  try {
    await request({
      service: "self",
      method: "post",
      url: `${HUBSPOT_SERVICE_URL}/firebasesignin`,
      body: {
        account,
        hubspotutk: getHubspotUtk(),
        environment: IS_DEPLOYMENT_ENV_TEST ? "test" : "production",
      },
    });
  } catch (e) {}
}

export async function informHubspotAccountCreation(account: any, id: string) {
  try {
    await request({
      service: "self",
      method: "post",
      url: `${HUBSPOT_SERVICE_URL}/accountcreated`,
      body: {
        account: { ...account, id: id },
        hubspotutk: getHubspotUtk(),
        environment: IS_DEPLOYMENT_ENV_TEST ? "test" : "production",
      },
    });
  } catch (e) {}
}

export function getHubspotUtk() {
  let hubspotutk = "";
  try {
    let _hubspotutk = document.cookie
      .split(" ")
      .find((c) => c.startsWith("hubspotutk"))!
      .split("=")[1];
    if (_hubspotutk.endsWith(";")) {
      _hubspotutk = _hubspotutk.split(";")[0];
    }
    hubspotutk = _hubspotutk;
  } catch (e) {}
  return hubspotutk;
}
